/**
 * Bulk Schedule Modal
 * Schedule multiple posts at once with custom times
 */

import { useState, useEffect } from 'react';
import { apiClient } from '../lib/api';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { Icon } from './icons';
import { format } from 'date-fns';
import type { Post } from '../types';

interface BulkScheduleModalProps {
  postIds: string[];
  onClose: () => void;
  onComplete: () => void;
}

interface ScheduleItem {
  postId: string;
  post: Post | null;
  scheduledAt: number;
}

export function BulkScheduleModal({ postIds, onClose, onComplete }: BulkScheduleModalProps) {
  const [scheduleItems, setScheduleItems] = useState<ScheduleItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setSaving] = useState(false);
  const [isLoadingBestTimes, setIsLoadingBestTimes] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bestTimesAnalysis, setBestTimesAnalysis] = useState<string | null>(null);

  useEffect(() => {
    fetchPosts();
  }, [postIds]);

  const fetchPosts = async () => {
    try {
      const posts = await Promise.all(
        postIds.map(id => apiClient.getPost(id))
      );

      // Initialize with 2-hour intervals starting from now
      const baseTime = Date.now();
      const items = posts.map((post, index) => ({
        postId: post.data!.id,
        post: post.data!,
        scheduledAt: baseTime + (index * 2 * 60 * 60 * 1000), // 2 hours apart
      }));

      setScheduleItems(items);
    } catch (err) {
      setError('Failed to load posts');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const updateSchedule = (postId: string, scheduledAt: number) => {
    setScheduleItems(items =>
      items.map(item =>
        item.postId === postId ? { ...item, scheduledAt } : item
      )
    );
  };

  const handleBulkSchedule = async () => {
    try {
      setSaving(true);
      setError(null);

      // Send bulk update request
      await apiClient.bulkUpdatePosts(
        scheduleItems.map(item => ({
          id: item.postId,
          scheduled_at: item.scheduledAt,
        }))
      );

      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to schedule posts');
      setSaving(false);
    }
  };

  const applyInterval = (hours: number) => {
    const baseTime = Date.now();
    setScheduleItems(items =>
      items.map((item, index) => ({
        ...item,
        scheduledAt: baseTime + (index * hours * 60 * 60 * 1000),
      }))
    );
  };

  const applyBestTimes = async () => {
    try {
      setIsLoadingBestTimes(true);
      setError(null);
      setBestTimesAnalysis(null);

      // Get unique platforms from all posts
      const allPlatforms = new Set<string>();
      scheduleItems.forEach(item => {
        item.post?.platforms.forEach(p => allPlatforms.add(p));
      });

      // Get brand_id from first post
      const brandId = scheduleItems[0]?.post?.brand_id;
      if (!brandId) {
        throw new Error('No brand found');
      }

      const response = await apiClient.getBestPostingTimes({
        brand_id: brandId,
        platforms: Array.from(allPlatforms),
        date_range: {
          start: Date.now(),
          end: Date.now() + (7 * 24 * 60 * 60 * 1000), // 7 days
        },
      });

      if (response.success && response.data) {
        const { suggestions, analysis } = response.data;

        if (suggestions && suggestions.length > 0) {
          // Apply suggestions to posts
          setScheduleItems(items =>
            items.map((item, index) => {
              // Find best matching suggestion for this post's platforms
              const matchingSuggestion = suggestions.find(s =>
                item.post?.platforms.includes(s.platform as any)
              ) || suggestions[index % suggestions.length];

              return {
                ...item,
                scheduledAt: matchingSuggestion.time,
              };
            })
          );

          setBestTimesAnalysis(analysis || 'AI-powered optimal posting times applied');
        } else {
          throw new Error('No time suggestions received');
        }
      } else {
        throw new Error(response.error || 'Failed to get best times');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get best times');
    } finally {
      setIsLoadingBestTimes(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-6">
      <Card className="max-w-4xl w-full max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Icon name="calendar" size="lg" />
              Bulk Schedule Posts
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Schedule {postIds.length} post{postIds.length > 1 ? 's' : ''} with custom times
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <Icon name="x" size="lg" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
              {error}
            </div>
          )}

          {/* Quick Actions */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Quick spacing:</span>
              <Button size="sm" variant="outline" onClick={() => applyInterval(1)}>
                1 hour apart
              </Button>
              <Button size="sm" variant="outline" onClick={() => applyInterval(2)}>
                2 hours apart
              </Button>
              <Button size="sm" variant="outline" onClick={() => applyInterval(4)}>
                4 hours apart
              </Button>
              <Button size="sm" variant="outline" onClick={() => applyInterval(24)}>
                1 day apart
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">AI-powered:</span>
              <Button
                size="sm"
                variant="outline"
                onClick={applyBestTimes}
                isLoading={isLoadingBestTimes}
                disabled={isLoadingBestTimes}
                className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200 hover:border-purple-300"
              >
                <Icon name="sparkles" size="sm" className="mr-1 text-purple-600" />
                Use Best Times
              </Button>
              {bestTimesAnalysis && (
                <span className="text-xs text-green-600 flex-1">
                  {bestTimesAnalysis}
                </span>
              )}
            </div>
          </div>

          {/* Posts List */}
          {isLoading ? (
            <div className="text-center py-12 text-gray-500">Loading posts...</div>
          ) : (
            <div className="space-y-4">
              {scheduleItems.map((item, index) => (
                <div
                  key={item.postId}
                  className="p-4 border border-gray-200 rounded-lg hover:border-gray-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold">
                      {index + 1}
                    </div>

                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 mb-1">
                        {item.post?.title || 'Untitled Post'}
                      </h3>
                      <p className="text-sm text-gray-600 line-clamp-1 mb-3">
                        {item.post?.content}
                      </p>

                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            <Icon name="clock" size="xs" className="inline mr-1" />
                            Schedule Date & Time
                          </label>
                          <input
                            type="datetime-local"
                            value={format(new Date(item.scheduledAt), "yyyy-MM-dd'T'HH:mm")}
                            onChange={(e) => updateSchedule(
                              item.postId,
                              new Date(e.target.value).getTime()
                            )}
                            min={format(new Date(), "yyyy-MM-dd'T'HH:mm")}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>

                        <div className="text-xs text-gray-500">
                          {format(new Date(item.scheduledAt), 'MMM d, yyyy h:mm a')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkSchedule}
            isLoading={isSaving}
            disabled={isLoading || isSaving}
          >
            Schedule {postIds.length} Post{postIds.length > 1 ? 's' : ''}
          </Button>
        </div>
      </Card>
    </div>
  );
}
